/**
 * React hook for P2P peer management
 * Supports universe namespacing for isolated peer networks
 * Uses signal relay for remote peers, BroadcastChannel for local tabs
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { PeerRegistry, Signal } from '@amorphous/bootstrap';
import { PeerManager, DAGSync, DAGStore, SignalHandler, DAGObject, KEEPALIVE_CHANNEL, CALL_CHANNEL, CallSignal } from '@amorphous/core';

const RELAY_CHANNEL = 'signal-relay';

// Signaling server URL
const SIGNAL_SERVER_URL = 'https://dnull.com/cgi-bin/signal.cgi';

// Default universe for this app
const DEFAULT_UNIVERSE = 'amorphous';

// Auto-reconnect configuration
const RECONNECT_BASE_DELAY_MS = 1000;   // Start with 1 second
const RECONNECT_MAX_DELAY_MS = 60000;   // Max 60 seconds
const RECONNECT_MAX_ATTEMPTS = 5;       // Give up after 5 attempts

// Periodic sync configuration
const PERIODIC_SYNC_INTERVAL_MS = 60000; // Re-sync with peers every 60 seconds

// Connection limits
const MAX_PEERS = 10;                    // Maximum number of peer connections

interface PeerInfo {
  id: string;
  state: 'discovered' | 'connecting' | 'connected' | 'disconnected';
  lastSeen: number;
}

interface UsePeersOptions {
  universeId?: string;
  signalServerUrl?: string;
  onObjectReceived?: (hashes: string[]) => void;
  onCallSignal?: (fromPeer: string, signal: CallSignal) => void;
}

interface UsePeersResult {
  peers: PeerInfo[];
  connectedCount: number;
  universeId: string;
  isReady: boolean;
  connect: (peerId: string) => Promise<void>;
  disconnect: (peerId: string) => void;
  broadcast: (channel: string, data: string) => void;
  broadcastObject: (obj: DAGObject) => void;
  announce: () => void;
  /** Request specific content from peers (for on-demand chunk fetching) */
  fetchContent: (hashes: string[]) => Promise<boolean>;
  /** Send a call signal (SDP/ICE) to a specific peer */
  sendCallSignal: (peerId: string, signal: CallSignal) => boolean;
}

export function usePeers(
  myId: string,
  dagStore: DAGStore,
  options: UsePeersOptions = {}
): UsePeersResult {
  const universeId = options.universeId || DEFAULT_UNIVERSE;
  const signalServerUrl = options.signalServerUrl || SIGNAL_SERVER_URL;
  const [peers, setPeers] = useState<Map<string, PeerInfo>>(new Map());
  const [isReady, setIsReady] = useState(false);

  const registryRef = useRef<PeerRegistry | null>(null);
  const peerManagerRef = useRef<PeerManager | null>(null);
  const dagSyncRef = useRef<DAGSync | null>(null);
  const syncedPeersRef = useRef<Set<string>>(new Set());

  // Track reconnect state per peer
  const reconnectStateRef = useRef<Map<string, { attempts: number; timer: ReturnType<typeof setTimeout> | null }>>(new Map());

  // Schedule a reconnect attempt with exponential backoff
  const scheduleReconnect = useCallback((peerId: string) => {
    const state = reconnectStateRef.current.get(peerId) || { attempts: 0, timer: null };

    // Clear any existing timer
    if (state.timer) {
      clearTimeout(state.timer);
    }

    // Check if we've exceeded max attempts
    if (state.attempts >= RECONNECT_MAX_ATTEMPTS) {
      console.log(`[P2P] Max reconnect attempts reached for ${peerId.slice(0, 8)}, giving up`);
      reconnectStateRef.current.delete(peerId);
      return;
    }

    // Calculate delay with exponential backoff
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * Math.pow(2, state.attempts),
      RECONNECT_MAX_DELAY_MS
    );

    console.log(`[P2P] Scheduling reconnect to ${peerId.slice(0, 8)} in ${delay}ms (attempt ${state.attempts + 1}/${RECONNECT_MAX_ATTEMPTS})`);

    const timer = setTimeout(async () => {
      // Check if peer is already connected (maybe they reconnected to us)
      const existingPeer = peerManagerRef.current?.getPeer(peerId);
      if (existingPeer && existingPeer.state === 'connected') {
        console.log(`[P2P] Peer ${peerId.slice(0, 8)} already reconnected, cancelling`);
        reconnectStateRef.current.delete(peerId);
        return;
      }

      console.log(`[P2P] Attempting reconnect to ${peerId.slice(0, 8)}...`);
      state.attempts++;
      reconnectStateRef.current.set(peerId, { ...state, timer: null });

      try {
        await peerManagerRef.current?.connect(peerId);
      } catch (e) {
        console.warn(`[P2P] Reconnect failed for ${peerId.slice(0, 8)}:`, e);
        // Schedule next attempt
        scheduleReconnect(peerId);
      }
    }, delay);

    reconnectStateRef.current.set(peerId, { attempts: state.attempts, timer });
  }, []);

  // Cancel reconnect for a peer (e.g., when they successfully reconnect)
  const cancelReconnect = useCallback((peerId: string) => {
    const state = reconnectStateRef.current.get(peerId);
    if (state?.timer) {
      clearTimeout(state.timer);
    }
    reconnectStateRef.current.delete(peerId);
  }, []);

  // Sync DAG with a peer (only once per peer for initial sync)
  const syncWithPeer = useCallback(async (peerId: string) => {
    if (!dagSyncRef.current || !peerManagerRef.current) return;

    // Prevent duplicate syncs with the same peer (for initial connection only)
    if (syncedPeersRef.current.has(peerId)) {
      console.log(`[P2P] Already synced with ${peerId.slice(0, 8)}, skipping`);
      return;
    }
    syncedPeersRef.current.add(peerId);

    console.log(`[P2P] Starting DAG sync with ${peerId.slice(0, 8)}...`);
    const haveMsg = await dagSyncRef.current.createHaveMessage();
    peerManagerRef.current.send(peerId, 'sync', JSON.stringify(haveMsg));
  }, []);

  // Periodic sync with all connected peers (catches missed broadcasts)
  const syncWithAllPeers = useCallback(async () => {
    if (!dagSyncRef.current || !peerManagerRef.current) return;

    const connectedPeers = peerManagerRef.current.getConnectedPeerIds();
    if (connectedPeers.length === 0) return;

    console.log(`[P2P] Periodic sync with ${connectedPeers.length} peers...`);
    const haveMsg = await dagSyncRef.current.createHaveMessage();
    const msgStr = JSON.stringify(haveMsg);

    for (const peerId of connectedPeers) {
      peerManagerRef.current.send(peerId, 'sync', msgStr);
    }
  }, []);

  // Initialize registry and peer manager
  useEffect(() => {
    if (!myId) return;

    let mounted = true;

    // Create registry with server URL and universe
    const registry = new PeerRegistry(myId, {
      serverUrl: signalServerUrl,
      universeId: universeId
    });
    registryRef.current = registry;

    console.log(`[P2P] Joining universe: ${universeId}`);

    // Create DAG sync handler
    dagSyncRef.current = new DAGSync(dagStore);

    // Create signal handler for PeerManager
    // Uses registry for local peers (BroadcastChannel) and relay for remote peers
    // Also sends via registry as HTTP fallback for all peers
    const signalHandler: SignalHandler = {
      sendOffer: (to: string, sdp: string) => {
        console.log(`[P2P] Sending offer to ${to.slice(0, 8)}...`);
        // Always send via registry (BroadcastChannel for local, HTTP for all)
        registry.sendOffer(to, sdp);
        // Also send via relay if peer is remote and we have connected peers
        if (!registry.isLocalPeer(to)) {
          const sent = peerManagerRef.current?.sendOfferViaRelay(to, sdp);
          if (sent) {
            console.log(`[P2P] Also relayed offer to ${to.slice(0, 8)}`);
          }
        }
      },
      sendAnswer: (to: string, sdp: string) => {
        console.log(`[P2P] Sending answer to ${to.slice(0, 8)}...`);
        // Always send via registry (BroadcastChannel for local, HTTP for all)
        registry.sendAnswer(to, sdp);
        // Also send via relay if peer is remote and we have connected peers
        if (!registry.isLocalPeer(to)) {
          const sent = peerManagerRef.current?.sendAnswerViaRelay(to, sdp);
          if (sent) {
            console.log(`[P2P] Also relayed answer to ${to.slice(0, 8)}`);
          }
        }
      },
      sendIceCandidate: (to: string, candidate: RTCIceCandidate) => {
        // Always send via registry
        registry.sendIceCandidate(
          to,
          candidate.candidate ?? '',
          candidate.sdpMid ?? undefined,
          candidate.sdpMLineIndex ?? undefined
        );
        // Also send via relay if peer is remote and we have connected peers
        if (!registry.isLocalPeer(to)) {
          peerManagerRef.current?.sendIceCandidateViaRelay(to, candidate);
        }
      },
    };

    // Create PeerManager with connection limit
    peerManagerRef.current = new PeerManager(myId, signalHandler, {
      onPeerConnected: (peerId: string) => {
        console.log(`[P2P] Connected to ${peerId.slice(0, 8)}...`);
        registry.setPeerState(peerId, 'connected');
        // Cancel any pending reconnect - we're connected now
        cancelReconnect(peerId);
        setPeers(prev => {
          const next = new Map(prev);
          const peer = next.get(peerId);
          if (peer) {
            next.set(peerId, { ...peer, state: 'connected' });
          }
          return next;
        });
        // Don't sync here - wait for channel open
      },
      onChannelOpen: (peerId: string, channel: string) => {
        console.log(`[P2P] Channel '${channel}' ready with ${peerId.slice(0, 8)}`);
        // Start DAG sync when sync channel is ready
        if (channel === 'sync') {
          syncWithPeer(peerId);
        }
      },
      onPeerDisconnected: (peerId: string) => {
        console.log(`[P2P] Disconnected from ${peerId.slice(0, 8)}...`);
        registry.setPeerState(peerId, 'disconnected');
        // Allow resync if peer reconnects
        syncedPeersRef.current.delete(peerId);
        setPeers(prev => {
          const next = new Map(prev);
          const peer = next.get(peerId);
          if (peer) {
            next.set(peerId, { ...peer, state: 'disconnected' });
          }
          return next;
        });
        // Schedule reconnect attempt
        scheduleReconnect(peerId);
      },
      onMessage: async (peerId: string, channelName: string, data: ArrayBuffer | string) => {
        // Update activity timestamp on any message
        peerManagerRef.current?.touchPeer(peerId);

        if (channelName === 'sync' && dagSyncRef.current) {
          const { response, stored } = await dagSyncRef.current.handleMessage(data as string);
          if (response) {
            peerManagerRef.current?.send(peerId, 'sync', JSON.stringify(response));
          }
          // Notify about newly received objects
          if (stored.length > 0 && options.onObjectReceived) {
            options.onObjectReceived(stored);
          }
        } else if (channelName === RELAY_CHANNEL) {
          // Handle relayed signals from other peers
          console.log(`[P2P] Received relay signal from ${peerId.slice(0, 8)}`);
          peerManagerRef.current?.handleRelaySignal(peerId, data as string);
        } else if (channelName === KEEPALIVE_CHANNEL) {
          // Handle keepalive ping/pong
          peerManagerRef.current?.handleKeepalive(peerId, data as string);
        } else if (channelName === CALL_CHANNEL) {
          // Handle call signaling (SDP/ICE for media connections)
          peerManagerRef.current?.handleCallSignal(peerId, data as string);
        }
      },
      onCallSignal: (fromPeer: string, signal: CallSignal) => {
        // Forward call signals to the useCalls hook
        options.onCallSignal?.(fromPeer, signal);
      },
    }, { maxPeers: MAX_PEERS });

    // Start keepalive mechanism
    peerManagerRef.current.startKeepalive();

    // Auto-connect to bootstrap peers discovered from server
    registry.setOnBootstrapPeers(async (newPeers: string[]) => {
      console.log(`[P2P] Discovered ${newPeers.length} bootstrap peers`);
      for (const peerId of newPeers) {
        // Skip if already connected or at capacity
        if (peerManagerRef.current?.getPeer(peerId)) continue;
        if (peerManagerRef.current?.isAtCapacity()) {
          console.log(`[P2P] At capacity, skipping bootstrap peer ${peerId.slice(0, 8)}`);
          break;
        }

        console.log(`[P2P] Auto-connecting to bootstrap peer ${peerId.slice(0, 8)}...`);
        setPeers(prev => {
          const next = new Map(prev);
          next.set(peerId, {
            id: peerId,
            state: 'connecting',
            lastSeen: Date.now(),
          });
          return next;
        });
        await peerManagerRef.current?.connect(peerId);
      }
    });

    // Set callback for incoming signals
    registry.setOnSignal((signal: Signal) => {
      if (!mounted) return;
      handleIncomingSignal(signal);
    });

    // Announce ourselves
    console.log(`[P2P] Announcing as ${myId.slice(0, 8)}...`);
    registry.announce();

    setIsReady(true);

    return () => {
      mounted = false;
      // Clear all reconnect timers
      for (const state of reconnectStateRef.current.values()) {
        if (state.timer) {
          clearTimeout(state.timer);
        }
      }
      reconnectStateRef.current.clear();
      registry.destroy();
      peerManagerRef.current?.disconnectAll();
    };
  }, [myId, dagStore, universeId, signalServerUrl, syncWithPeer, scheduleReconnect, cancelReconnect]);

  // Periodic sync interval - catches missed broadcasts and ensures consistency
  useEffect(() => {
    if (!isReady) return;

    const interval = setInterval(() => {
      syncWithAllPeers();
    }, PERIODIC_SYNC_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isReady, syncWithAllPeers]);

  // Handle incoming signal
  const handleIncomingSignal = useCallback(async (signal: Signal) => {
    const msgType = signal.type;
    const sourcePeer = signal.sourcePeer;

    // Skip our own messages
    if (sourcePeer === myId) return;

    console.log(`[P2P] Received ${msgType} from ${sourcePeer?.slice(0, 8) ?? 'unknown'}`);

    switch (msgType) {
      case 'announce':
        if (sourcePeer) {
          setPeers(prev => {
            const next = new Map(prev);
            if (!next.has(sourcePeer)) {
              next.set(sourcePeer, {
                id: sourcePeer,
                state: 'discovered',
                lastSeen: Date.now(),
              });
            } else {
              const peer = next.get(sourcePeer)!;
              next.set(sourcePeer, { ...peer, lastSeen: Date.now() });
            }
            return next;
          });

          // Reply with our announce
          registryRef.current?.announce();
        }
        break;

      case 'offer':
        if (sourcePeer) {
          const sdp = signal.payload.sdp;
          if (sdp) {
            console.log(`[P2P] Received offer from ${sourcePeer.slice(0, 8)}...`);
            setPeers(prev => {
              const next = new Map(prev);
              next.set(sourcePeer, {
                id: sourcePeer,
                state: 'connecting',
                lastSeen: Date.now(),
              });
              return next;
            });
            await peerManagerRef.current?.handleOffer(sourcePeer, sdp);
          }
        }
        break;

      case 'answer':
        if (sourcePeer) {
          const sdp = signal.payload.sdp;
          if (sdp) {
            console.log(`[P2P] Received answer from ${sourcePeer.slice(0, 8)}...`);
            await peerManagerRef.current?.handleAnswer(sourcePeer, sdp);
          }
        }
        break;

      case 'ice_candidate':
        if (sourcePeer) {
          const payload = signal.payload;
          if (payload.candidate) {
            console.log(`[P2P] Received ICE from ${sourcePeer.slice(0, 8)}...`);
            await peerManagerRef.current?.handleIceCandidate(
              sourcePeer,
              payload.candidate,
              payload.sdp_mid,
              payload.sdp_m_line_index
            );
          }
        }
        break;

      case 'disconnect':
        if (sourcePeer) {
          setPeers(prev => {
            const next = new Map(prev);
            next.delete(sourcePeer);
            return next;
          });
        }
        break;
    }
  }, [myId]);

  // Connect to a peer
  const connect = useCallback(async (peerId: string) => {
    console.log(`[P2P] Initiating connection to ${peerId.slice(0, 8)}...`);
    setPeers(prev => {
      const next = new Map(prev);
      const peer = next.get(peerId);
      if (peer) {
        next.set(peerId, { ...peer, state: 'connecting' });
      }
      return next;
    });

    await peerManagerRef.current?.connect(peerId);
  }, []);

  // Disconnect from a peer
  const disconnect = useCallback((peerId: string) => {
    peerManagerRef.current?.disconnect(peerId);
    setPeers(prev => {
      const next = new Map(prev);
      next.delete(peerId);
      return next;
    });
  }, []);

  // Broadcast to all peers
  const broadcast = useCallback((channel: string, data: string) => {
    peerManagerRef.current?.broadcast(channel, data);
  }, []);

  // Broadcast a new DAG object to all peers
  // Only broadcasts metadata - large content (chunks) are NOT broadcast
  const broadcastObject = useCallback((obj: DAGObject) => {
    if (!dagSyncRef.current) return;
    const msg = dagSyncRef.current.createBroadcastMessage(obj);
    // createBroadcastMessage returns null for objects that shouldn't be broadcast
    if (msg) {
      peerManagerRef.current?.broadcast('sync', JSON.stringify(msg));
    }
  }, []);

  // Fetch specific content on-demand from peers (for large content like chunks)
  const fetchContent = useCallback(async (hashes: string[]): Promise<boolean> => {
    if (!dagSyncRef.current || !peerManagerRef.current) return false;
    if (hashes.length === 0) return true;

    const connectedPeers = peerManagerRef.current.getConnectedPeerIds();
    if (connectedPeers.length === 0) {
      console.warn('[P2P] No connected peers to fetch content from');
      return false;
    }

    // Request content from first connected peer
    // TODO: Could parallelize across multiple peers for faster downloads
    const fetchMsg = dagSyncRef.current.createFetchMessage(hashes);
    const peerId = connectedPeers[0];

    console.log(`[P2P] Requesting ${hashes.length} chunks from ${peerId.slice(0, 8)}`);
    peerManagerRef.current.send(peerId, 'sync', JSON.stringify(fetchMsg));

    // The response will come back via onMessage handler
    // Return true to indicate request was sent (caller should poll for availability)
    return true;
  }, []);

  // Announce presence
  const announce = useCallback(() => {
    registryRef.current?.announce();
  }, []);

  // Send call signal to a specific peer
  const sendCallSignal = useCallback((peerId: string, signal: CallSignal): boolean => {
    return peerManagerRef.current?.sendCallSignal(peerId, signal) ?? false;
  }, []);

  const peerList = Array.from(peers.values());
  const connectedCount = peerList.filter(p => p.state === 'connected').length;

  // Update registry with connected peer count for adaptive polling
  useEffect(() => {
    registryRef.current?.setConnectedPeerCount(connectedCount);
  }, [connectedCount]);

  return {
    peers: peerList,
    connectedCount,
    universeId,
    isReady,
    connect,
    disconnect,
    broadcast,
    broadcastObject,
    announce,
    fetchContent,
    sendCallSignal,
  };
}
